import React, { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store/hooks/redux';
import { ReactNode } from 'react';
import styles from './index.module.scss'
import { alertsSlice, AlertType } from 'app/store/reducers/alertsSlice';
import clsx from 'clsx';
import { CloseIcon } from 'shared/ui/icons/icons';
import { store } from 'app/ui';

export type IAlertProps = {
    children: ReactNode
};

export const AlertModal = ({children}: IAlertProps) => {
    const errors = useAppSelector(state => state.alertsReducer);
    const {removeAlert, startRemoveAlert} = alertsSlice.actions;
    const dispatch = useAppDispatch();
    const [hoveredAlerts, setHoveredAlerts] = useState<Record<string, boolean>>({});
    
    // Track hover start times and total pause durations
    const hoverStartTimes = useRef<Record<string, number>>({});
    const pauseDurations = useRef<Record<string, number>>({});
    
    // Effect to track when an alert starts being hovered
    useEffect(() => {
        Object.entries(hoveredAlerts).forEach(([id, isHovered]) => {
            if (isHovered) {
                // Record when hover started
                hoverStartTimes.current[id] = Date.now();
            } else if (hoverStartTimes.current[id]) {
                // Calculate and accumulate pause duration when hover ends
                const hoverDuration = Date.now() - hoverStartTimes.current[id];
                pauseDurations.current[id] = (pauseDurations.current[id] || 0) + hoverDuration;
                delete hoverStartTimes.current[id];
            }
        });
    }, [hoveredAlerts]);
    
    // Single checker interval for all alerts
    useEffect(() => {
        const intervalId = setInterval(() => {
            const now = Date.now();
            
            errors.forEach(alert => {
                // Skip if already being removed
                if (alert.isStartedRemove) {
                    return;
                }
                
                // Calculate effective elapsed time, accounting for pauses
                let effectiveElapsedTime = now - alert.startDate;
                
                // Subtract accumulated pause time
                effectiveElapsedTime -= (pauseDurations.current[alert.identifier] || 0);
                
                // If currently hovering, subtract current hover duration too
                if (hoveredAlerts[alert.identifier] && hoverStartTimes.current[alert.identifier]) {
                    effectiveElapsedTime -= (now - hoverStartTimes.current[alert.identifier]);
                }
                
                // If effective time exceeds duration, remove the alert
                if (effectiveElapsedTime >= alert.duration) {
                    // Mark alert for removal
                    dispatch(startRemoveAlert(alert.identifier));
                    
                    // Apply closing animation
                    const alertElement = document.querySelector(`[data-alert-id="${alert.identifier}"]`);
                    if (alertElement) {
                        alertElement.classList.add(styles.closing);
                        
                        // Remove after animation completes
                        setTimeout(() => {
                            dispatch(removeAlert(alert.identifier));
                            // Clean up refs
                            delete pauseDurations.current[alert.identifier];
                            delete hoverStartTimes.current[alert.identifier];
                        }, 300);
                    } else {
                        dispatch(removeAlert(alert.identifier));
                        // Clean up refs
                        delete pauseDurations.current[alert.identifier];
                        delete hoverStartTimes.current[alert.identifier];
                    }
                }
            });
            
            // Clean up references for alerts that no longer exist
            const currentAlertIds = new Set(errors.map(alert => alert.identifier));
            [...Object.keys(pauseDurations.current), ...Object.keys(hoverStartTimes.current)].forEach(id => {
                if (!currentAlertIds.has(id)) {
                    delete pauseDurations.current[id];
                    delete hoverStartTimes.current[id];
                }
            });
        }, 100);
        
        return () => clearInterval(intervalId);
    }, [errors, hoveredAlerts, dispatch]);
    
    const handleClose = (identifier: string) => {
        // Mark alert for removal
        dispatch(startRemoveAlert(identifier));
        
        // Apply closing animation
        const alertElement = document.querySelector(`[data-alert-id="${identifier}"]`);
        if (alertElement) {
            alertElement.classList.add(styles.closing);
            
            // Remove after animation completes
            setTimeout(() => {
                dispatch(removeAlert(identifier));
                // Clean up refs
                delete pauseDurations.current[identifier];
                delete hoverStartTimes.current[identifier];
            }, 300);
        } else {
            dispatch(removeAlert(identifier));
            // Clean up refs
            delete pauseDurations.current[identifier];
            delete hoverStartTimes.current[identifier];
        }
    };
    
    const handleMouseEnter = (identifier: string) => {
        setHoveredAlerts(prev => ({ ...prev, [identifier]: true }));
    };
    
    const handleMouseLeave = (identifier: string) => {
        setHoveredAlerts(prev => ({ ...prev, [identifier]: false }));
    };
    
    const alertType = (alertType : AlertType) => {
        switch(alertType) {
            case AlertType.Error:  
                return styles.error;
            case AlertType.Success:  
                return styles.ok;
            case AlertType.Warning:  
                return styles.warning;
            default:
                return styles.error;
        }
    }
    
    return (
        <>
            {errors.length > 0 && 
                <div className={styles.container}>
                    {errors.map((el) => {
                        // Calculate effective progress for the animation
                        const now = Date.now();
                        let elapsedTime = now - el.startDate;
                        elapsedTime -= (pauseDurations.current[el.identifier] || 0);
                        
                        // If currently hovering, subtract current hover time too
                        if (hoveredAlerts[el.identifier] && hoverStartTimes.current[el.identifier]) {
                            elapsedTime -= (now - hoverStartTimes.current[el.identifier]);
                        }
                        
                        // Calculate remaining percentage (0-100)
                        const remainingPercent = Math.max(0, Math.min(100, (1 - elapsedTime / el.duration) * 100));
                        
                        return (
                            <div 
                                key={"alert" + el.identifier} 
                                data-alert-id={el.identifier}
                                className={clsx(styles.alertWindow, alertType(el.type))}
                                onMouseEnter={() => handleMouseEnter(el.identifier)}
                                onMouseLeave={() => handleMouseLeave(el.identifier)}
                            >
                                <div 
                                    className={styles.close} 
                                    onClick={() => handleClose(el.identifier)}
                                >
                                    <CloseIcon stroke='colorCurrent' />
                                </div>
                                {el.message}
                                <div 
                                    style={{
                                        width: `${remainingPercent}%`,
                                        animationPlayState: hoveredAlerts[el.identifier] ? 'paused' : 'running',
                                        animationDuration: `${el.duration / 1000}s`,
                                    }} 
                                    className={styles.decreasingLine}
                                />
                            </div>
                        );
                    })}
                </div>
            }
            {children}    
        </> 
    );
}
